import type { SeasonalWear } from './seasons.ts';

/**
 * Primitives shared by every Organised Fun Centre game: rooms, players, names and
 * the connection envelope. Nothing game-specific belongs here.
 */

export const PROTOCOL_VERSION = 2;

/** Which game a room belongs to. The room directory maps a code to one of these. */
export type GameId =
  | 'movie'
  | 'quiz'
  | 'quiplash'
  | 'fibbage'
  | 'enough'
  | 'wavelengths'
  | 'obscurity'
  | 'superlatives'
  | 'chameleon'
  | 'hottakes'
  // Solo and duel games, all run by one kind of room: see arcade.ts.
  | 'higherlower'
  | 'castreveal'
  | 'posterzoom'
  | 'sixdegrees'
  | 'connect4'
  | 'noughts'
  | 'battleships'
  | 'dots'
  | 'wordduel'
  | 'anagrams'
  | 'poker'
  | 'liarsdice'
  | 'whosaid'
  | 'wouldyourather'
  | 'cheat'
  | 'rankem'
  | 'spotlight'
  | 'blindranking'
  | 'onestar'
  | 'wagerquiz'
  | 'fakinit'
  | 'undercover'
  | 'mole'
  | 'boxart';

export const GAME_IDS: GameId[] = [
  'movie',
  'quiz',
  'quiplash',
  'fibbage',
  'enough',
  'wavelengths',
  'obscurity',
  'superlatives',
  'chameleon',
  'hottakes',
  'higherlower',
  'castreveal',
  'posterzoom',
  'sixdegrees',
  'connect4',
  'noughts',
  'battleships',
  'dots',
  'wordduel',
  'anagrams',
  'poker',
  'liarsdice',
  'whosaid',
  'wouldyourather',
  'cheat',
  'rankem',
  'spotlight',
  'blindranking',
  'onestar',
  'wagerquiz',
  'fakinit',
  'undercover',
  'mole',
  'boxart',
];

export function isGameId(value: string): value is GameId {
  return (GAME_IDS as string[]).includes(value);
}

export type PlayerId = string;

/** The fields every game's player has. Games extend this with their own. */
export interface Player {
  id: PlayerId;
  name: string;
  connected: boolean;
  /** Set when the player is signed in; drives stat tracking. */
  userId?: string;
  /** Neon colour token for their name plate. */
  color: string;
  emoji: string;
  /** The backing their name sits on. Absent means the plain one. */
  plate?: PlayerPlate;
  /**
   * They have a picture uploaded, which the client fetches by their `userId`.
   * The bytes are never put in game state: a roster goes out on every join and
   * every reconnect, and a dozen inlined images would dwarf the game itself.
   */
  picture?: boolean;
  /** An earned title, by achievement id, shown under their name. */
  title?: string | null;
  /** The effect that plays for everyone when their moment comes. */
  flourish?: string;
  /** The typeface their name is set in. Absent means the house one. */
  font?: string;
  /** The ring around their avatar. */
  frame?: string;
  /** A line shown under their name when they win, by catalogue id. */
  catchphrase?: string | null;
  /** How their card arrives in a lobby. */
  entrance?: string;
  /** The backdrop behind their name card. */
  banner?: string;
  /** When their phone dropped, while it is away: a short absence keeps the seat. */
  awaySince?: number;
  /**
   * Missed two rounds running, so the room has stopped waiting for them. Any
   * action brings them straight back. Absent means playing.
   */
  sittingOut?: boolean;
  /** Rounds missed in a row, so the room knows when to stop waiting. */
  missed?: number;
}

/** Everything a player wears, as rooms copy it from an account onto a seat. */
export type Appearance = Pick<
  Player,
  'plate' | 'picture' | 'title' | 'flourish' | 'font' | 'frame' | 'catchphrase' | 'entrance' | 'banner'
>;

/** A signed-in player's public profile. Guests have none. */
export interface Account {
  id: string;
  username: string;
  avatarColor: string;
  avatarEmoji: string;
  avatarPlate?: PlayerPlate;
  /** Whether they have uploaded a picture. */
  avatarPicture?: boolean;
  avatarTitle?: string | null;
  avatarFlourish?: string;
  /** How the app looks on their own phone. Nobody else sees it. */
  skin?: string;
  avatarFont?: string;
  avatarFrame?: string;
  avatarCatchphrase?: string | null;
  avatarEntrance?: string;
  avatarBanner?: string;
  /** A skin chosen for one game in particular, overriding `skin` inside it. */
  gameSkins?: Partial<Record<GameId, string>>;
  /**
   * What they have put on for the event that is on now, over the top of the
   * rest. Null outside an event, so it can never outlast one.
   */
  seasonal?: SeasonalWear | null;
}

// ---------------------------------------------------------------------------
// Messages every room understands
// ---------------------------------------------------------------------------

/** What can be thrown at the screen during a reveal. */
export const REACTIONS = ['😂', '🔥', '😱', '👏', '💀', '🤯'] as const;
export type Reaction = (typeof REACTIONS)[number];

/** Per player: this many reactions in this long, so nobody can flood a room. */
export const REACTION_BURST = 6;
export const REACTION_WINDOW_MS = 4_000;

export function isReaction(value: unknown): value is Reaction {
  return typeof value === 'string' && (REACTIONS as readonly string[]).includes(value);
}

/**
 * Set while the host has the clock stopped. Belongs to the phase it was taken in:
 * a room clears it the moment that phase gives way or a fresh deadline is set, so a
 * stale pause can never freeze the next round.
 */
export interface PauseState {
  remainingMs: number;
  phase: string;
}

/** Extra fields any room's `hello` may carry. */
export interface HelloExtras {
  /** A big shared screen: sees everything public, holds no seat, plays nothing. */
  spectator?: boolean;
  /**
   * One of the audience: watches like a big screen, holds no seat, but may vote
   * in a voting game for a small "audience favourite" bonus. `audienceId` is kept
   * on the phone so a reconnect is still one vote.
   */
  audience?: boolean;
  audienceId?: string;
  /** The game night this room belongs to, if any. */
  lounge?: string;
}

export type CommonClientMessage =
  | { t: 'react'; emoji: string }
  | { t: 'pause' }
  | { t: 'resume' }
  /** Host only: throw this prompt away and deal a fresh one. */
  | { t: 'skip' };

/** Rounds missed in a row before a player is marked as sitting out. */
/** How many people beyond the players a voting game lets watch and vote. */
export const AUDIENCE_MAX = 100;
/** What the audience's favourite earns: small, so a crowd cannot decide a game. */
export const AUDIENCE_BONUS = 50;

/** How many are in the audience, sent to everyone whenever it changes. */
export interface AudienceCountMessage {
  t: 'audience';
  count: number;
}

/** What the audience made of a vote, sent as the reveal opens. */
export interface AudienceResultMessage {
  t: 'audienceResult';
  /** How many in the audience voted. */
  total: number;
  /** What they backed most, or null for nobody, or a tie. */
  favourite: { label: string; votes: number; bonus: number } | null;
  /** A line for when there is no favourite to name. */
  note?: string;
}

/** Why a room turned somebody away at the door. */
export type RefusalReason = 'full' | 'kicked' | 'name' | 'version';

/**
 * Sent instead of a welcome when a room will not let you in, just before the
 * socket closes. The client stops retrying and says what to do next.
 */
export interface RefusedMessage {
  t: 'refused';
  reason: RefusalReason;
  message: string;
}

export const SIT_OUT_AFTER = 2;

/** How long a round leaderboard stays up before moving on by itself. */
export const AUTO_ADVANCE_MS = 12_000;

// ---------------------------------------------------------------------------
// Prompts the room writes itself
// ---------------------------------------------------------------------------

/** A prompt somebody in the room wrote, for the games that take them. */
export interface CustomPrompt {
  /** `c:<n>`, so it can never collide with a catalogue id, which is a hash. */
  id: string;
  text: string;
  authorId: PlayerId;
  authorName: string;
}

export const CUSTOM_PROMPTS_PER_PLAYER = 5;
export const CUSTOM_PROMPTS_PER_ROOM = 40;
export const CUSTOM_PROMPT_MIN_LENGTH = 6;
export const CUSTOM_PROMPT_MAX_LENGTH = 100;
/** The chance each round draws one of the room's own, while any are left. */
export const CUSTOM_PROMPT_CHANCE = 1 / 3;

export type CustomPromptClientMessage =
  | { t: 'addPrompt'; text: string }
  /** The author can take back their own; the host can remove anyone's. */
  | { t: 'removePrompt'; id: string };

/**
 * Deliberately unsequenced: a reaction is decoration, and numbering it would make
 * every phone that missed one think it had missed a move.
 */
export type CommonServerMessage =
  | { t: 'reaction'; playerId: PlayerId; emoji: string }
  | AudienceCountMessage
  | AudienceResultMessage;

/** Every stateful message carries a monotonic seq; a gap makes the client resync. */
export interface Seqd {
  seq: number;
  serverTime: number;
  /** The sequence at the game's last real change, not counting the player list. */
  stage?: number;
}

// ---------------------------------------------------------------------------
// Room codes
// ---------------------------------------------------------------------------

/** Unambiguous alphabet: no O/0 or I/1 mix-ups when a code is read aloud at a party. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRTUVWXY2346789';
export const ROOM_CODE_LENGTH = 4;

export function isValidRoomCode(code: string): boolean {
  if (code.length !== ROOM_CODE_LENGTH) return false;
  for (const ch of code) if (!ROOM_CODE_ALPHABET.includes(ch)) return false;
  return true;
}

export function newRoomCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(ROOM_CODE_LENGTH));
  return Array.from(bytes, (b) => ROOM_CODE_ALPHABET[b % ROOM_CODE_ALPHABET.length]).join('');
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

export const MAX_NAME_LENGTH = 20;
export const MIN_NAME_LENGTH = 1;

/**
 * A deliberately small blocklist. This is a party game among friends, not a public
 * service: the aim is to stop a name nobody wants read aloud, not to be exhaustive.
 * Matched against a leet-folded, punctuation-stripped form so "f.u.c.k" and "fvck"
 * are caught too.
 */
const BLOCKED = [
  'fuck',
  'shit',
  'cunt',
  'bitch',
  'bastard',
  'wanker',
  'nigger',
  'nigga',
  'faggot',
  'retard',
  'rape',
  'nazi',
  'hitler',
];

/** Folds common substitutions so a blocklist hit cannot be dodged with symbols. */
function foldForMatching(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[0@]/g, 'o')
    .replace(/[1!|]/g, 'i')
    .replace(/3/g, 'e')
    .replace(/[4]/g, 'a')
    .replace(/[5$]/g, 's')
    .replace(/7/g, 't')
    .replace(/[^a-z]/g, '');
}

/**
 * Innocent words that happen to contain a blocked one — the Scunthorpe problem.
 * These are removed before matching, so a real place or country is not rejected.
 * Substring matching is kept because it is what catches "bigshitlord"; this list
 * is the price of that, and will need the occasional addition.
 */
const ALLOWED = [
  'scunthorpe',
  'shitterton',
  'mishit',
  'nigeria',
  'nigerian',
  'niger',
  'grape',
  'drape',
  'scrape',
  'therapeutic',
  'rapeseed',
  'assassin',
];

export function isProfane(name: string): boolean {
  let folded = foldForMatching(name);
  for (const safe of ALLOWED) folded = folded.split(safe).join('');
  return BLOCKED.some((word) => folded.includes(word));
}

/** Collapses whitespace and trims to the maximum length. */
export function cleanName(raw: string): string {
  return String(raw ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_NAME_LENGTH);
}

export type NameRejection = 'empty' | 'too_long' | 'duplicate' | 'profane';

export interface NameCheck {
  ok: boolean;
  /** The cleaned name, when ok. */
  name: string;
  reason?: NameRejection;
  message?: string;
}

/**
 * Validates a player name against the room. `taken` is every name already in the
 * room, compared case-insensitively so "Sam" and "sam" cannot both join.
 */
export function checkName(raw: string, taken: readonly string[] = []): NameCheck {
  const name = cleanName(raw);

  if (name.length < MIN_NAME_LENGTH) {
    return { ok: false, name, reason: 'empty', message: 'Enter a name first.' };
  }
  if (raw.trim().length > MAX_NAME_LENGTH) {
    return {
      ok: false,
      name,
      reason: 'too_long',
      message: `Names are at most ${MAX_NAME_LENGTH} characters.`,
    };
  }
  if (isProfane(name)) {
    return { ok: false, name, reason: 'profane', message: 'Pick a different name.' };
  }
  const lower = name.toLowerCase();
  if (taken.some((other) => other.toLowerCase() === lower)) {
    return { ok: false, name, reason: 'duplicate', message: 'Someone already has that name.' };
  }

  return { ok: true, name };
}

// ---------------------------------------------------------------------------
// Cosmetics
// ---------------------------------------------------------------------------

/** Neon tube colours available for a player's name plate. */
export const NEON_COLORS = [
  '#ff2d95',
  '#22e0ff',
  '#ffc247',
  '#7cff4f',
  '#c46bff',
  '#ff6b3d',
  '#4fffd4',
  '#ff4f6b',
] as const;

/**
 * The first ten are the defaults a guest is dealt, in order, so an unclaimed
 * room still looks like the cinema it is themed on. The rest are there to be
 * chosen by anyone signed in.
 */
export const PLAYER_EMOJI = [
  '🎬', '🍿', '🎞️', '⭐', '🕶️', '🎤', '🎟️', '🍸', '🎰', '🛼',
  '👻', '🐙', '🦊', '🐸', '🦖', '🦩', '🐝', '🦈', '🐧', '🦉',
  '🍕', '🌮', '🍩', '🍒', '🥑', '☕', '🍦', '🧀',
  '🚀', '🛸', '🎸', '🎩', '💀', '🔮', '⚡', '🌈',
  '🧊', '🌵', '🪐', '🎲',
] as const;

/**
 * The plate a player's name sits on. Signed-in players may pick one; everybody
 * else gets the plain one.
 *
 * Tokens rather than colours, because the client renders each as a gradient in
 * the player's own neon colour — so a plate never fights the palette, and the
 * colourblind settings keep working.
 */
export const PLAYER_PLATES = [
  'plain',
  'glow',
  'stripes',
  'grid',
  'sunset',
  'static',
  'carbon',
  'marquee',
] as const;

/**
 * Plates that move, and are earned rather than given. They are kept out of
 * PLAYER_PLATES so the free set a guest can pick from stays exactly as it was.
 * All of them stop dead under reduced motion, like every other animation here.
 */
export const EARNED_PLATES = [
  'shimmer',
  'pulse',
  'scan',
  'aurora',
  'inferno',
  'gold',
  'holo',
  // Added later. The three free ones sit here too, so the original free set
  // above stays exactly as it was.
  'dots',
  'waves',
  'chevron',
  'checker',
  'lava',
  'matrix',
  'galaxy',
  'prism',
  'camo',
  'flames',
  'platinum',
] as const;

export type PlayerPlate = (typeof PLAYER_PLATES)[number] | (typeof EARNED_PLATES)[number];

export function isPlayerPlate(value: string): value is PlayerPlate {
  return (
    (PLAYER_PLATES as readonly string[]).includes(value) ||
    (EARNED_PLATES as readonly string[]).includes(value)
  );
}

/** How big an uploaded picture may be once encoded, in bytes. */
export const MAX_PICTURE_BYTES = 48_000;
/** What the client scales a picture down to before sending it. */
export const PICTURE_SIZE = 128;

/** First unused option, falling back to a rotation once every option is taken. */
export function firstFree(options: readonly string[], taken: ReadonlySet<string>, index: number): string {
  return options.find((option) => !taken.has(option)) ?? options[index % options.length]!;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
